import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { CustelOTPInput } from '../../components/ui/CustelOTPInput';

/**
 * T-MAIL Faz 1 — Login e-posta OTP doğrulama sayfası.
 *
 * İki amaç (login yanıtındaki `purpose`):
 *   - FIRST_LOGIN_VERIFY → ilk girişte e-posta doğrulama (welcome@'tan kod)
 *   - LOGIN_MFA          → yetkili rol login 2. faktörü
 *
 * `state`: { challenge, email, purpose } — LoginPage'den gelir.
 */
type Purpose = 'FIRST_LOGIN_VERIFY' | 'LOGIN_MFA';

export function EmailOtpPage() {
  const { t } = useTranslation('auth');
  const location = useLocation();
  const navigate = useNavigate();
  const fetchSession = useAuthStore((s) => s.fetchSession);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  const state = (location.state as { email?: string; challenge?: string; purpose?: Purpose } | null) ?? {};
  const { email = '', challenge = '', purpose = 'FIRST_LOGIN_VERIFY' } = state;

  // Resend cooldown sayaç
  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, [resendIn]);

  if (!email || !challenge) {
    navigate('/giris');
    return null;
  }

  const isFirstLogin = purpose === 'FIRST_LOGIN_VERIFY';
  const title = isFirstLogin ? t('emailOtp.firstLogin.title') : t('emailOtp.mfa.title');
  const description = isFirstLogin
    ? t('emailOtp.firstLogin.description', { email })
    : t('emailOtp.mfa.description', { email });

  async function onSubmit(value: string) {
    if (!/^\d{6}$/.test(value)) {
      toast.error(t('emailOtp.toast.codeLength'));
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/auth/email-otp/verify', { challenge, code: value });
      await fetchSession();
      toast.success(t('emailOtp.toast.loginSuccess'));
      navigate('/');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('emailOtp.toast.verifyFailed'));
      setCode('');
    } finally {
      setSubmitting(false);
    }
  }

  async function onResend() {
    try {
      const res = await api.post<{ cooldownSeconds?: number }>('/auth/email-otp/resend', { challenge });
      setResendIn(res.cooldownSeconds ?? 60);
      toast.success(t('emailOtp.toast.codeSent'));
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t('emailOtp.toast.sendFailed');
      toast.error(message);
      // 429 → backend "X sn sonra" mesajı; basit cooldown başlat
      setResendIn(60);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-custel-bg-subtle p-8">
      <div className="w-full max-w-md rounded-custel border border-custel-border bg-custel-surface p-8 shadow-sm">
        <h1 className="mb-2 text-2xl font-semibold text-custel-text">{title}</h1>
        <p className="mb-6 text-sm text-custel-text-secondary">{description}</p>

        <div className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium text-custel-text">{t('emailOtp.field.code')}</label>
            <CustelOTPInput value={code} onChange={setCode} onComplete={onSubmit} disabled={submitting} autoFocus />
          </div>

          <button
            type="button"
            onClick={() => onSubmit(code)}
            className="btn-primary w-full"
            disabled={submitting || code.length !== 6}
          >
            {submitting ? t('emailOtp.submitting') : t('emailOtp.submit')}
          </button>
        </div>

        <div className="mt-6 flex items-center justify-between text-sm">
          <button
            type="button"
            onClick={onResend}
            disabled={resendIn > 0}
            className="text-custel-g700 underline-offset-2 hover:text-custel-ink hover:underline disabled:cursor-not-allowed disabled:text-custel-text-tertiary disabled:no-underline"
          >
            {resendIn > 0 ? t('emailOtp.resendCountdown', { seconds: resendIn }) : t('emailOtp.resend')}
          </button>
          <Link to="/giris" className="text-custel-g700 underline-offset-2 hover:text-custel-ink hover:underline">
            {t('emailOtp.backToLogin')}
          </Link>
        </div>
      </div>
    </main>
  );
}
